import { Button } from "@/components/ui/button";
import { AlertTriangle, FileWarning } from "lucide-react";
import type { SameFileImport } from "@/@types/dtfe-import";

interface DuplicateConfirmModalProps {
  existingCount: number;
  sameFileImports?: SameFileImport[];
  onReplace: () => void;
  onAppend: () => void;
  onCancel: () => void;
}

const formatImportedAt = (value: string) =>
  new Date(value).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

export const DuplicateConfirmModal = ({
  existingCount,
  sameFileImports = [],
  onReplace,
  onAppend,
  onCancel,
}: DuplicateConfirmModalProps) => {
  const hasSameFile = sameFileImports.length > 0;

  return (
    <div className="flex flex-col gap-4">
      {hasSameFile && (
        <div className="flex gap-3 rounded-md border border-red-200 bg-red-50 p-3">
          <FileWarning className="h-5 w-5 text-red-500 shrink-0" />
          <div className="flex flex-col gap-1 text-sm text-red-700">
            <p>
              <strong>Este arquivo já foi importado</strong> e continua ativo.
              Incluir novamente vai duplicar os valores na conciliação.
            </p>
            <ul className="list-disc pl-4">
              {sameFileImports.map((item) => (
                <li key={item.batch_id}>
                  {item.file_name} — {formatImportedAt(item.imported_at)}
                  {item.imported_by ? ` por ${item.imported_by}` : ""} (
                  {item.active_count} registros ativos)
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div className="flex items-center gap-3">
        <AlertTriangle className="h-6 w-6 text-yellow-500 shrink-0" />
        <p className="text-sm text-gray-700">
          Já existem <strong>{existingCount}</strong> registros para esta data.
          O que deseja fazer?
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <Button
          className="bg-red-500 text-white hover:bg-red-600 w-full"
          onClick={onReplace}
        >
          Substituir (apagar existentes e importar novos)
        </Button>
        <Button
          className="bg-[#F6D040] text-[#613F4F] hover:bg-[#F6D040]/90 w-full"
          onClick={onAppend}
        >
          Incluir (manter existentes e adicionar novos)
        </Button>
        <Button variant="outline" className="w-full" onClick={onCancel}>
          Cancelar
        </Button>
      </div>
    </div>
  );
};

interface ConfirmActionModalProps {
  actionLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export const ConfirmActionModal = ({
  actionLabel,
  onConfirm,
  onCancel,
}: ConfirmActionModalProps) => {
  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-gray-700">
        Tem certeza que deseja <strong>{actionLabel}</strong>? Esta ação não pode
        ser desfeita.
      </p>
      <div className="flex gap-3 justify-end">
        <Button variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
        <Button
          className="bg-[#613F4F] text-white hover:bg-[#613F4F]/90"
          onClick={onConfirm}
        >
          Confirmar
        </Button>
      </div>
    </div>
  );
};
